<script>
import { cloneDeep } from 'lodash';
import { isBefore, parseISO, startOfDay } from 'date-fns';
import { required, custom, and, withMessage } from 'form-validation-manager';

import MessagesApi from '@/api/messages';

export default {
  name: 'MessageFormRow',
  props: {
    message: {
      type: Object,
      required: true
    },
    textesList: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    loading: true
  }),
  validations: {
    message: {
      texte: withMessage(required(), 'Valeur requise'),
      dateDebut: withMessage(required(), 'Valeur requise'),
      dateFin: and(
        withMessage(required(), 'Valeur requise'),
        withMessage(
          custom(function (val) {
            const dateDebut = this.message.dateDebut;
            if (!val || !dateDebut) {
              return false;
            }
            return isBefore(parseISO(val), parseISO(dateDebut)) ? 'error' : false;
          }),
          'La date de fin doit être supérieure ou égale à la date de début'
        )
      )
    }
  },
  methods: {
    valider () {
      this.$fvm.validate(true);
      this.$refs.combobox.$emit('blur');
      this.$refs.form.validate(true);
      if (this.$fvm.$error) {
        return;
      }

      this.loading = true;
      (
        this.message.__new
          ? MessagesApi.addMessage(this.message).then(m => { this.message.id = m.id; })
          : MessagesApi.modifyMessage(this.message)
      ).then(() => {
        delete this.message.__new;
        delete this.message.__modification;
        delete this.message.__originalValue;
        this.$forceUpdate();
      }).finally(() => { this.loading = false; });
    },
    annuler () {
      delete this.message.__modification;
      Object.assign(this.message, this.message.__originalValue);
      delete this.message.__originalValue;
      this.$forceUpdate();
    },
    modifier () {
      Object.defineProperty(this.message, '__modification', { configurable: true, writable: true, value: true });
      Object.defineProperty(this.message, '__originalValue', { configurable: true, value: cloneDeep(this.message) });
      this.$forceUpdate();
    },
    supprimer () {
      if (!this.message.__new) {
        this.loading = true;
        MessagesApi.deleteMessage(this.message).then(() => {
          this.$emit('delete');
        }).finally(() => { this.loading = false; });
      } else {
        this.$emit('delete');
      }
    },
    allowedDates (key) {
      switch (key) {
        case 'DEBUT':
          return (date) => {
            return !isBefore(parseISO(date), startOfDay(Date.now()));
          };
        case 'FIN':
          return (date) => {
            return !isBefore(parseISO(date), startOfDay(Date.now())) && (this.message.dateDebut ? !isBefore(parseISO(date), parseISO(this.message.dateDebut)) : true);
          };
      }
    }
  }
};
</script>

<template>
  <tr>
    <td>
      <v-form
        ref="form"
        class="inputs"
      >
        <div>
          <v-combobox
            ref="combobox"
            v-model="message.texte"
            :disabled="!message.__modification"
            label="Message"
            :items="textesList"
            :rules="$fvm.message.texte.$errors"
            hide-no-data
          />
        </div>
        <div>
          <DateField
            v-model="message.dateDebut"
            :disabled="!message.__modification"
            label="Date début"
            class="mx-12"
            :rules="$fvm.message.dateDebut.$errors"
            :allowed-dates="allowedDates('DEBUT')"
            close-on-select
          />
          <DateField
            v-model="message.dateFin"
            :disabled="!message.__modification"
            :allowed-dates="allowedDates('FIN')"
            label="Date fin"
            class="mx-12"
            :rules="$fvm.message.dateFin.$errors"
            close-on-select
          />
        </div>
      </v-form>
    </td>
    <td>
      <div class="actions">
        <v-btn
          v-if="message.__modification"
          v-show="!message.__new"
          outlined
          @click="annuler()"
        >
          Annuler <v-icon>mdi-undo</v-icon>
        </v-btn>
        <v-btn
          v-else
          @click="modifier()"
        >
          Modifier <v-icon>mdi-pencil</v-icon>
        </v-btn>
        <v-btn
          :disabled="!message.__modification"
          @click="valider()"
        >
          Valider <v-icon>mdi-content-save</v-icon>
        </v-btn>
        <v-btn
          color="error"
          @click="supprimer()"
        >
          Supprimer <v-icon>mdi-delete</v-icon>
        </v-btn>
      </div>
    </td>
  </tr>
</template>

<style lang="scss" scoped>
td{
  border-bottom: 2em solid transparent;
}

.inputs{
  display: flex;
  flex-direction: column;
  width: 800px;

  &>*{
    display: flex;
    flex-direction:row;
    justify-content: space-around;
  }
}

.actions{
  display: flex;
  flex-direction: column;
  margin-left:2em;;

  &>*:not(:last-child){
    margin-bottom: 0.7em;
  }
}
</style>
